/**
 * Phase 4 — Provider / Utility Creator: Utility Approval workflow page.
 * Route: /broker/utility-approval
 * Endpoints:
 *   GET   /api/provider/utilities
 *   POST  /api/provider/utilities                     (save draft or submit)
 *   PATCH /api/provider/utilities/{id}                (edit draft / changes_requested)
 *   POST  /api/provider/utilities/{id}/submit         (submit / resubmit)
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Send, Pencil, X, RefreshCw, ShieldCheck, Clock, AlertTriangle,
  CheckCircle2, XCircle, PauseCircle, Archive,
} from 'lucide-react';
import theme from '../theme';
import SafeLanguageWarnings from '../components/SafeLanguageWarnings';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UTILITY_TYPES = [
  'discount', 'digital_resource', 'exclusive_content', 'service_credit',
  'consultation', 'event_access', 'community_access', 'product_bonus',
  'membership_benefit', 'employee_benefit', 'referral_reward',
  'partner_offer', 'physical_perk', 'other',
];

const FULFILLMENT_OPTIONS = [
  'provider_fulfills', 'business_fulfills', 'third_party_fulfills',
  'platform_assisted', 'depends_on_utility',
];

const FREE_OR_PAID = ['free', 'paid', 'mixed'];

const STATUS_META = {
  draft:              { color: theme.colors.text.muted,      icon: Pencil,        label: 'Draft' },
  submitted:          { color: theme.colors.status.info,     icon: Send,          label: 'Submitted' },
  under_review:       { color: theme.colors.status.warning,  icon: Clock,         label: 'Under Review' },
  changes_requested:  { color: theme.colors.status.warning,  icon: AlertTriangle, label: 'Changes Requested' },
  approved:           { color: theme.colors.status.success,  icon: CheckCircle2,  label: 'Approved' },
  rejected:           { color: theme.colors.status.error,    icon: XCircle,       label: 'Rejected' },
  suspended:          { color: theme.colors.status.error,    icon: PauseCircle,   label: 'Suspended' },
  archived:           { color: theme.colors.text.muted,      icon: Archive,       label: 'Archived' },
};

const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, padding: 20, boxShadow: theme.colors.shadows.sm,
};
const inputStyle = {
  width: '100%', padding: '9px 11px', border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.sm, fontSize: 13, outline: 'none', boxSizing: 'border-box',
};

const EMPTY_FORM = {
  utility_name: '', utility_type: 'discount', utility_description: '',
  free_or_paid: 'free', fulfillment_responsibility: 'provider_fulfills',
  fulfillment_instructions: '', eligibility_rules: '', limits_conditions: '',
  expiration_rules: '', website_or_example_link: '',
};

export default function ProviderUtilitiesApproval() {
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }), []);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [safeWarnings, setSafeWarnings] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/provider/utilities`, auth);
      setRows(res.data?.utilities || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load utilities');
    } finally { setLoading(false); }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const startCreate = () => {
    setEditingId(null); setForm(EMPTY_FORM); setShowForm(true);
  };

  const startEdit = (u) => {
    setEditingId(u.id);
    setForm({
      utility_name: u.utility_name || '', utility_type: u.utility_type || 'discount',
      utility_description: u.utility_description || '',
      free_or_paid: u.free_or_paid || 'free',
      fulfillment_responsibility: u.fulfillment_responsibility || 'provider_fulfills',
      fulfillment_instructions: u.fulfillment_instructions || '',
      eligibility_rules: u.eligibility_rules || '',
      limits_conditions: u.limits_conditions || '',
      expiration_rules: u.expiration_rules || '',
      website_or_example_link: u.website_or_example_link || '',
    });
    setShowForm(true);
  };

  const save = async (asDraft) => {
    if (!form.utility_name.trim() || !form.utility_description.trim()) {
      toast.error('Utility Name and Description are required'); return;
    }
    setSaving(true);
    try {
      let res;
      if (editingId) {
        res = await axios.patch(`${API}/provider/utilities/${editingId}`, form, auth);
        toast.success('Utility updated');
      } else {
        res = await axios.post(`${API}/provider/utilities`, { ...form, save_as_draft: asDraft }, auth);
        toast.success(asDraft ? 'Draft saved' : 'Utility submitted for review');
      }
      const w = res.data?.safe_language_warnings || [];
      setSafeWarnings(w);
      if (w.length === 0) { setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const submitForReview = async (id) => {
    try {
      await axios.post(`${API}/provider/utilities/${id}/submit`, {}, auth);
      toast.success('Submitted for review');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submit failed');
    }
  };

  const StatusPill = ({ status }) => {
    const meta = STATUS_META[status] || { color: theme.colors.text.muted, icon: ShieldCheck, label: status };
    const Icon = meta.icon;
    return (
      <span data-testid={`util-status-${status}`}
        style={{ padding: '3px 10px', borderRadius: 999, background: `${meta.color}1A`,
          color: meta.color, fontSize: 11, fontWeight: 800, textTransform: 'uppercase',
          display: 'inline-flex', gap: 4, alignItems: 'center' }}>
        <Icon size={12} /> {meta.label}
      </span>
    );
  };

  return (
    <div data-testid="provider-utilities-page" style={{ padding: '24px 28px', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 6 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ShieldCheck size={22} color={theme.colors.primary} />
            <div style={{ fontSize: 22, fontWeight: 900, color: theme.colors.text.primary }}>My Utilities</div>
          </div>
          <div style={{ color: theme.colors.text.muted, marginTop: 4, maxWidth: 720, fontSize: 13 }}>
            Submit and manage utilities, perks, services, rewards, and access offers that may be
            reviewed for use inside the Kickback Contract ecosystem.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button data-testid="provider-utilities-refresh-btn" onClick={load}
            style={{ padding: '9px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borders}`,
              background: theme.colors.bg.card, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={14} /> Refresh
          </button>
          <button data-testid="provider-create-utility-btn" onClick={startCreate}
            style={{ padding: '9px 14px', borderRadius: theme.radius.sm, border: 'none',
              background: theme.colors.primary, color: '#fff', cursor: 'pointer', fontWeight: 700,
              display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Plus size={14} /> Create Utility
          </button>
        </div>
      </div>

      {showForm && (
        <div data-testid="provider-utility-form" style={{ ...cardStyle, marginTop: 16 }}>
          <SafeLanguageWarnings warnings={safeWarnings} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontWeight: 800, fontSize: 15 }}>{editingId ? 'Edit Utility' : 'New Utility'}</div>
            <button onClick={() => { setShowForm(false); setEditingId(null); }}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
              <X size={16} />
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginBottom: 10 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700 }}>Utility Name *</label>
              <input data-testid="util-name" style={inputStyle}
                value={form.utility_name} onChange={e => setForm({ ...form, utility_name: e.target.value })} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700 }}>Utility Type</label>
              <select data-testid="util-type" style={inputStyle}
                value={form.utility_type} onChange={e => setForm({ ...form, utility_type: e.target.value })}>
                {UTILITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700 }}>Free or Paid</label>
              <select data-testid="util-free-paid" style={inputStyle}
                value={form.free_or_paid} onChange={e => setForm({ ...form, free_or_paid: e.target.value })}>
                {FREE_OR_PAID.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700 }}>Who Fulfills This Utility</label>
              <select data-testid="util-fulfillment" style={inputStyle}
                value={form.fulfillment_responsibility}
                onChange={e => setForm({ ...form, fulfillment_responsibility: e.target.value })}>
                {FULFILLMENT_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 11, fontWeight: 700 }}>Description *</label>
            <textarea data-testid="util-description" style={{ ...inputStyle, minHeight: 70, fontFamily: 'inherit' }}
              value={form.utility_description}
              onChange={e => setForm({ ...form, utility_description: e.target.value })} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginBottom: 10 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700 }}>Fulfillment Instructions</label>
              <textarea data-testid="util-instructions" style={{ ...inputStyle, minHeight: 60, fontFamily: 'inherit' }}
                value={form.fulfillment_instructions}
                onChange={e => setForm({ ...form, fulfillment_instructions: e.target.value })} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700 }}>Eligibility Rules</label>
              <textarea data-testid="util-eligibility" style={{ ...inputStyle, minHeight: 60, fontFamily: 'inherit' }}
                value={form.eligibility_rules}
                onChange={e => setForm({ ...form, eligibility_rules: e.target.value })} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700 }}>Limits, Rules, or Conditions</label>
              <textarea data-testid="util-limits" style={{ ...inputStyle, minHeight: 60, fontFamily: 'inherit' }}
                value={form.limits_conditions}
                onChange={e => setForm({ ...form, limits_conditions: e.target.value })} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700 }}>Expiration Rules</label>
              <textarea data-testid="util-expiration" style={{ ...inputStyle, minHeight: 60, fontFamily: 'inherit' }}
                value={form.expiration_rules}
                onChange={e => setForm({ ...form, expiration_rules: e.target.value })} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700 }}>Website or Example Link</label>
              <input data-testid="util-link" style={inputStyle}
                value={form.website_or_example_link}
                onChange={e => setForm({ ...form, website_or_example_link: e.target.value })} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {!editingId && (
              <button data-testid="util-save-draft-btn" disabled={saving} onClick={() => save(true)}
                style={{ padding: '10px 14px', borderRadius: theme.radius.sm,
                  border: `1px solid ${theme.colors.borders}`, background: theme.colors.bg.card,
                  cursor: 'pointer', fontWeight: 700 }}>
                Save Draft
              </button>
            )}
            <button data-testid="util-submit-btn" disabled={saving} onClick={() => save(false)}
              style={{ padding: '10px 14px', borderRadius: theme.radius.sm, border: 'none',
                background: theme.colors.status.success, color: '#fff', cursor: 'pointer', fontWeight: 700 }}>
              {editingId ? 'Save Changes' : 'Submit for Review'}
            </button>
          </div>
        </div>
      )}

      <div style={{ ...cardStyle, marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontWeight: 800, fontSize: 15 }}>Your Utilities</div>
          <div data-testid="util-count" style={{ fontSize: 12, color: theme.colors.text.muted }}>
            {rows.length} total
          </div>
        </div>
        {loading ? (
          <div data-testid="provider-utilities-loading" style={{ padding: 20, color: theme.colors.text.muted }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div data-testid="provider-utilities-empty" style={{ padding: 24, textAlign: 'center', color: theme.colors.text.muted }}>
            No utilities yet. Click <b>Create Utility</b> to submit your first one.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {rows.map(u => {
              const editable = u.status === 'draft' || u.status === 'changes_requested';
              const canSubmit = u.status === 'draft' || u.status === 'changes_requested' || u.status === 'rejected';
              return (
                <div key={u.id} data-testid={`util-row-${u.id}`}
                  style={{ display: 'flex', gap: 12, padding: 12, background: theme.colors.bg.secondary,
                    borderRadius: theme.radius.sm, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 260 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <div style={{ fontWeight: 800, color: theme.colors.text.primary }}>{u.utility_name}</div>
                      <StatusPill status={u.status} />
                      <span style={{ fontSize: 11, color: theme.colors.text.muted }}>{u.utility_type}</span>
                    </div>
                    <div style={{ fontSize: 12, color: theme.colors.text.secondary, marginTop: 4 }}>
                      {u.utility_description}
                    </div>
                    {u.status === 'changes_requested' && u.requested_changes && (
                      <div data-testid={`util-changes-${u.id}`} style={{ marginTop: 8, padding: 10,
                        background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: theme.radius.sm,
                        fontSize: 12, color: '#78350F' }}>
                        <b>Admin requested changes:</b> {u.requested_changes}
                      </div>
                    )}
                    {u.status === 'rejected' && u.rejection_reason && (
                      <div data-testid={`util-rejection-${u.id}`} style={{ marginTop: 8, padding: 10,
                        background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: theme.radius.sm,
                        fontSize: 12, color: '#991B1B' }}>
                        <b>Rejected:</b> {u.rejection_reason}
                      </div>
                    )}
                    {u.admin_notes && (
                      <div style={{ marginTop: 8, fontSize: 11, color: theme.colors.text.muted }}>
                        Admin note: {u.admin_notes}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {editable && (
                      <button data-testid={`util-edit-${u.id}`} onClick={() => startEdit(u)}
                        style={{ padding: '6px 10px', borderRadius: theme.radius.sm,
                          border: `1px solid ${theme.colors.borders}`, background: theme.colors.bg.card,
                          cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                        <Pencil size={12} /> Edit
                      </button>
                    )}
                    {canSubmit && (
                      <button data-testid={`util-submit-${u.id}`} onClick={() => submitForReview(u.id)}
                        style={{ padding: '6px 10px', borderRadius: theme.radius.sm, border: 'none',
                          background: theme.colors.primary, color: '#fff', cursor: 'pointer',
                          display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700 }}>
                        <Send size={12} /> {u.status === 'draft' ? 'Submit' : 'Resubmit'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
